import { useState, useEffect } from "react";
import styled, { css } from "styled-components";
import { useParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import isMatch from "lodash.ismatch";
import { isMobile } from "react-device-detect";

import { useUser } from "components/helper/userContext";
import { useSession } from "components/helper/sessionContext";
import LoginPassword from "components/authentificationMode/LoginPassword";
import LoginEmailPasswordSMS from "components/authentificationMode/LoginEmailPasswordSMS";
import BasicForm from "components/authentificationMode/common/BasicForm";
import Layout from "components/common/Layout";
import {
    formFrame,
    onBackgroundImage,
} from "components/common/backgroundFrame";
import Spinner from "components/common/Spinner";
import ErrorPage from "components/common/ErrorPage";
import SendPINCode from "components/authentificationMode/SendPINCode";

import { useBrowserSupported } from "hooks/useBrowserSupported";

import { DisplayedError, NotFoundError, UnknownError } from "services/errors";
import { convertLineBreak } from "services/convertLineBreak";

import {
    FieldType,
    Scenario,
    VerificationMethodEnum,
} from "types/scenarioType";
import { getSessionConnectionInfo } from "components/helper/callApi";
import { SessionConnectionInfo } from "types/sessionType";
import { CSS_VARIABLES } from "enums/cssVariables";
import { ERROR } from "types/snackBar.constant";
import { Theme } from "types/voteEnum";

import {
    LS_BROWSER_SESSION_ID_KEY,
    LS_SESSION_KEY,
    LS_SESSION_LANG_KEY,
    LS_SIGNATURE_KEY,
} from "constants/constants";

const UserLoginPage = () => {
    const [sessionConnectionInfo, setSessionConnectionInfo] =
        useState<SessionConnectionInfo | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showSendPIN, setShowSendPIN] = useState(false);
    const [error, setError] = useState<DisplayedError | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const { t } = useTranslation();
    const { onlineCode } = useParams();
    const { user, setUser } = useUser();
    const { session, setSession } = useSession();
    const { enqueueSnackbar } = useSnackbar();
    const isBrowserSupported = useBrowserSupported();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const sessionConnectionData: SessionConnectionInfo =
                    await getSessionConnectionInfo(onlineCode!);
                setSessionConnectionInfo(sessionConnectionData);

                if (
                    user &&
                    user.onlineCode !== sessionConnectionData.codeOnline
                ) {
                    setUser(null);
                }

                if (session && !isMatch(session, sessionConnectionData)) {
                    setSession(null);
                    localStorage.removeItem(LS_SESSION_KEY);
                }

                if (sessionConnectionData) {
                    localStorage.setItem(
                        LS_SESSION_LANG_KEY,
                        sessionConnectionData.lang,
                    );

                    localStorage.setItem(
                        LS_BROWSER_SESSION_ID_KEY,
                        sessionConnectionData.sessionId,
                    );

                    localStorage.setItem(
                        LS_SIGNATURE_KEY,
                        sessionConnectionData.signature,
                    );
                }

                setIsLoading(false);
            } catch (e) {
                setIsLoading(false);
                if (e instanceof NotFoundError) {
                    setError(new NotFoundError());
                } else {
                    setError(new UnknownError());
                }
                enqueueSnackbar(`${t("errorUnknown")}`, {
                    variant: ERROR,
                });
            }
        };

        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onlineCode]);

    const handleError = (error: NotFoundError | UnknownError) => {
        setError(error);
    };

    const renderScenario = (scenario: Scenario) => {
        if (scenario.verificationMethods.includes(VerificationMethodEnum.SMS)) {
            return (
                <LoginEmailPasswordSMS
                    codeOnline={onlineCode as string}
                    scenario={scenario}
                    disabled={!isBrowserSupported}
                />
            );
        } else if (
            scenario.verificationMethods.includes(VerificationMethodEnum.EMAIL)
        ) {
            return (
                <LoginPassword
                    codeOnline={onlineCode as string}
                    scenario={scenario}
                    disabled={!isBrowserSupported}
                />
            );
        }

        const allowedFieldType: string[] = ["string"];

        const fields: FieldType[] = scenario.fields
            .filter((f) => allowedFieldType.includes(f.type))
            .map((f) => {
                let field: FieldType = f;

                if (f.name === "idReference") {
                    field = {
                        ...field,
                        showQRCode: isMobile,
                        showSendPINByEmail:
                            sessionConnectionInfo?.canSendPinByEmail,
                        onResendPINClick: () => setShowSendPIN(true),
                    };
                }

                return field;
            });

        return (
            <BasicForm
                fields={fields}
                successMessage={successMessage}
                errorMessage={errorMessage}
                codeOnline={onlineCode as string}
                scenario={scenario}
                onError={handleError}
                disabled={!isBrowserSupported}
            />
        );
    };

    function renderAuthContent() {
        if (isLoading) {
            return (
                <SpinnerWrapper>
                    <Spinner />
                </SpinnerWrapper>
            );
        }

        if (showSendPIN && onlineCode) {
            return (
                <SendPINCode
                    codeOnline={onlineCode}
                    returnBack={(error: string | null) => {
                        if (error) {
                            setErrorMessage(error);
                            setSuccessMessage(null);
                        } else {
                            setErrorMessage(null);
                            setSuccessMessage(t("pinEmailSent"));
                        }
                        setShowSendPIN(false);
                    }}
                    disabled={!isBrowserSupported}
                />
            );
        }

        return renderScenario(
            (sessionConnectionInfo as SessionConnectionInfo).scenario,
        );
    }

    // The secure e-voting guarantee only makes sense for Votebox, not for Quizzbox quizzes.
    const isReassuranceTextDisplayed =
        Theme.QUIZZBOX !== window.env.REACT_APP_THEME;

    if (error) {
        return <ErrorPage error={error} />;
    }

    if (!isLoading && user && session) {
        return <Navigate replace to={`/session/${onlineCode}`} />;
    }

    return (
        <>
            <Layout>
                <Wrapper>
                    <LoginFrame>
                        <FormWrapper>
                            <FormTitle>
                                {sessionConnectionInfo?.libelleEvent
                                    ? convertLineBreak(
                                          sessionConnectionInfo?.libelleEvent,
                                      )
                                    : t(
                                          `dynamicSessionTitle.${window.env.REACT_APP_THEME}`,
                                          { onlineCode },
                                      )}
                            </FormTitle>

                            {renderAuthContent()}
                        </FormWrapper>
                        {isReassuranceTextDisplayed && (
                            <ReassuranceText>
                                {`${t("loginReassuranceText", {
                                    AppName:
                                        window.env.REACT_APP_THEME.charAt(
                                            0,
                                        ).toUpperCase() +
                                        window.env.REACT_APP_THEME.slice(1),
                                })}`}
                            </ReassuranceText>
                        )}
                    </LoginFrame>
                </Wrapper>
            </Layout>
        </>
    );
};

const Wrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
`;

// Single frame around the form and the text; without a background image it only mirrors Wrapper's layout.
const LoginFrame = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    ${formFrame(css`
        width: 320px;
    `)}
`;

const ReassuranceText = styled.p`
    margin: auto;
    padding: 2rem 0 1rem 0;
    font-size: 0.75rem;
    line-height: 22px;
    width: 620px;
    @media (max-width: 768px) {
        width: 320px;
    }
    ${onBackgroundImage(css`
        width: auto;
        margin: 0;
        padding: 0.75rem 1.5rem 1rem 1.5rem;
        text-align: center;
    `)}
`;

const FormWrapper = styled.div`
    text-align: center;
    box-shadow: var(${CSS_VARIABLES.BOX_SHADOW_PIXEL})
        var(${CSS_VARIABLES.BORDER_FORM_COLOR});
    width: 320px;
    background-color: #fafafa;
    /* Inside the frame the form must not draw a second box. */
    ${onBackgroundImage(css`
        box-shadow: none;
        background-color: transparent;
    `)}
`;

const FormTitle = styled.div`
    font-size: 1.3rem;
    padding: 1.25rem 1rem;
    color: var(${CSS_VARIABLES.PRIMARY_TEXT_COLOR});
    background-color: var(${CSS_VARIABLES.BACKGROUND_FORM_COLOR});
`;

const SpinnerWrapper = styled.div`
    padding-bottom: 20px;
`;

export default UserLoginPage;
